import { Status } from "@/types/enums";
import { CropZone } from "@/types/cropZone";
import { FieldInsights } from "@/types/fieldInsights";

export type InsightsTableRow = FieldInsights & {
    crop_zone_id: number | null;
    crop_zone_name: string | null;
    crop_zone_color: string | null;
    row_key: string;
};

function activeCropZones(zones: CropZone[] | undefined): CropZone[] {
    return (zones ?? []).filter(
        (zone) => zone.status == null || zone.status === Status.Active,
    );
}

export function expandInsightsRows(
    fields: FieldInsights[],
): InsightsTableRow[] {
    return fields.flatMap((field): InsightsTableRow[] => {
        const zones = activeCropZones(field.crop_zones);
        if (zones.length <= 1) {
            return [
                {
                    ...field,
                    crop_zone_id: null,
                    crop_zone_name: null,
                    crop_zone_color: null,
                    row_key: String(field.id),
                },
            ];
        }
        return zones.map((zone) => {
            const planting = field.active_planting_operation;
            const cropName = zone.crop?.name;
            return {
                ...field,
                recommendation: zone.recommendation ?? field.recommendation,
                default_nitrogen_plan_simulation:
                    zone.default_nitrogen_plan_simulation ??
                    field.default_nitrogen_plan_simulation,
                n_applied_so_far:
                    zone.n_applied_so_far ?? field.n_applied_so_far,
                crop_stage: zone.crop_stage ?? field.crop_stage,
                active_planting_operation:
                    cropName && planting
                        ? {
                              ...planting,
                              crop_configuration: {
                                  ...planting.crop_configuration,
                                  id: Number(
                                      zone.crop?.id ??
                                          planting.crop_configuration.id,
                                  ),
                                  crop_name: cropName,
                              },
                          }
                        : planting,
                crop_zone_id: zone.id,
                crop_zone_name: zone.name,
                crop_zone_color: zone.color,
                row_key: `${field.id}-${zone.id}`,
            };
        });
    });
}

export function dedupeFieldsById<T extends { id: number }>(rows: T[]): T[] {
    const seen = new Set<number>();
    const unique: T[] = [];
    for (const row of rows) {
        if (seen.has(row.id)) continue;
        seen.add(row.id);
        unique.push(row);
    }
    return unique;
}
